/* eslint-disable
import/prefer-default-export,
no-useless-escape,
prefer-destructuring,
dot-notation,
no-param-reassign,
no-unused-vars,
no-undef,
no-extra-boolean-cast,
quote-props,
max-len
*/
// import { useCallback } from 'react';
import Cookies from 'js-cookie';
import { isBrowser, normalizeArrayParams, sendFlexibleFormEventImmediate, resolveAiAssistantMedium } from '../../../utilities/misc';
import { mergeDedupeSortAbTests } from './mergeAbTests';
// Flexible form analytics use sendFlexibleFormEventImmediate (not sendToGtm) so events are not
// dropped by misc.js’s debounced setInterval.
import {
  getBaseVariation,
  getAutonomousEndpointProductValue,
  isAutonomousProductSelection,
  isCombinedStyleTrialProductPicker,
  isAemRemoteSupportDualProductVariation,
  usesStandaloneAemTrialFields,
  isCheckboxCombinedVariation,
  isSingleSelectProductPickerVariation,
  checkboxProductQuantityField,
  CHECKBOX_PRODUCT_REMOTE_ACCESS,
  CHECKBOX_PRODUCT_REMOTE_SUPPORT,
  CHECKBOX_PRODUCT_AEM,
  filterValidSelectedProductValues,
} from './trialFormBase';
import {
  isRetrialVariation,
  isSbaVariation,
  isSosVariation,
  isAemVariation,
  isEnterpriseVariation,
  getLeadSource,
  resolveLeadSource,
} from './leadSourceUtils';

export const techniciansWarning = (content, thisVariation) => (
  isEnterpriseVariation(thisVariation)
    ? content?.messages?.warning?.['49981-how_many_technicians_provide_support_enterprise']
      || content?.messages?.warning?.['49981-how_many_technicians_provide_support']
    : content?.messages?.warning?.['49981-how_many_technicians_provide_support']
);

/**
 * Pick the trial-form subhead for the given variation.
 *
 * - Any retrial variation (`retrial`, `retrial-access`, `retrial-support`,
 *   `sos-retrial`, `sba-retrial`, `aem-retrial`, `retrial-aem`, …) renders
 *   `content.subhead_retrial` ("Restart Your Free Trial"), falling back to
 *   `fallbackSubhead` when Contentful hasn't authored the retrial copy.
 * - Non-retrial variations render `fallbackSubhead` (callers pass the
 *   AEM-landing-aware `subhead` they already compute).
 *
 * Historically this behavior lived inline in `TrialForm` / `TrialFormSteps`.
 * PR #880 narrowed the gate to AEM retrials only, which silently regressed
 * every other retrial page back to "Start Your Free Trial" — extracting the
 * selector makes the gate explicit and unit-testable.
 */
export const getTrialSubhead = (variation, content, fallbackSubhead) => (
  isRetrialVariation(variation)
    ? ((content && content.subhead_retrial) ?? fallbackSubhead)
    : fallbackSubhead
);

export {
  getBaseVariation,
  getAutonomousEndpointProductValue,
  AEM_AUTONOMOUS_PRODUCT_FORM_VALUE,
  isAutonomousProductSelection,
  remapCombinedAutonomousOptionToFormValue,
  isCombinedStyleTrialProductPicker,
  isAemRemoteSupportDualProductVariation,
  RETRIAL_AEM_VARIATION,
  isRetrialAemVariation,
  usesAemRetrialSubhead,
  usesAemRetrialSubmitLabel,
  usesStandaloneAemTrialFields,
  isCheckboxCombinedVariation,
  isSingleSelectProductPickerVariation,
  checkboxProductQuantityField,
  getCheckboxPrimaryProduct,
  isValidCheckboxSelectedProductValue,
  filterValidSelectedProductValues,
  isSelectedProductsFieldKey,
  shouldPostCheckboxSelectedProducts,
  sanitizeCheckboxFormValuesForApi,
  CHECKBOX_PRODUCT_REMOTE_ACCESS,
  CHECKBOX_PRODUCT_REMOTE_SUPPORT,
  CHECKBOX_PRODUCT_AEM,
} from './trialFormBase';
export {
  isRetrialVariation,
  isSbaVariation,
  isSosVariation,
  isAemVariation,
  isEnterpriseVariation,
  usesWorkEmailLabel,
  getLeadSource,
  resolveLeadSource,
  resolveCheckboxLeadSource,
} from './leadSourceUtils';

// useful Utility to trace rerenders
// cont useTraceUpdate = (props) => {
//   const prev = useRef(props);
//   useEffect(() => {
//     const changedProps = Object.entries(props).reduce((ps, [k, v]) => {
//       if (prev.current[k] !== v) {
//         ps[k] = [prev.current[k], v];
//       }
//       return ps;
//     }, {});
//     if (Object.keys(changedProps).length > 0) {
//       console.log('Checkbox Changed props:', changedProps);
//     }
//     prev.current = props;
//   });
// }

export const passwordRules = /^(?=.*\d)(?=.*[a-z])(?=.*[A-Z]).{8,}$/;
export const invalidWhiteSpace = /^\S*$/;

const getQueryStrings = () => {
  const params = {};
  if (isBrowser && window.location.search) {
    const queryParams = window.location.search.replace('?', '');

    if (queryParams) {
      queryParams.split('&').forEach((pair) => {
        const [key, value] = pair.split('=');
        params[decodeURIComponent(key)] = decodeURIComponent(value);
      });
    }
  }
  return params;
};

const updateParams = (obj) => {
  // remove following params
  // Support web_ parameters
  const platformValues = ['my', 'web', 'web-jp'];
  const paramObj = { ...obj };
  const webParams = {
    web_source: 'utm_source',
    web_medium: 'utm_medium',
    web_campaign: 'utm_campaign',
    web_content: 'utm_content',
    web_term: 'utm_term',
    web_device: 'utm_device'
  };
  Object.keys(webParams).forEach((key) => {
    if (key in paramObj) {
      if (!(webParams[key] in paramObj)) paramObj[webParams[key]] = paramObj[key];
      delete paramObj[key];
    }
  });

  // Recover `ai-assistant` when the source is a known AI host but the medium is
  // empty or defaulted to `direct` (AI tools such as ChatGPT tag utm_source but
  // strip the referrer, so detectReferrerInitials falls back to `direct`). This
  // covers direct deep-links to the trial form, which read only the page URL.
  const aiMedium = resolveAiAssistantMedium(paramObj.utm_source, paramObj.utm_medium);
  if (aiMedium !== paramObj.utm_medium) paramObj.utm_medium = aiMedium;

  if ('_ga' in paramObj) delete paramObj._ga;
  if ('_gac' in paramObj) delete paramObj._gac;
  if ('_gl' in paramObj) delete paramObj._gl;
  if ('__hstc' in paramObj) delete paramObj.__hstc;
  if ('__hssc' in paramObj) delete paramObj.__hssc;
  if ('__hsfp' in paramObj) delete paramObj.__hsfp;
  if ('ajs_aid' in paramObj) delete paramObj.ajs_aid;
  if ('ajs_uid' in paramObj) delete paramObj.ajs_uid;
  if ('optid' in paramObj) delete paramObj.optid;

  if (!('platform' in paramObj) || !(platformValues.includes(paramObj.platform))) {
    paramObj.platform = 'web';
  }
  return paramObj;
};

export const getTrialParams = () => {
  if (isBrowser) {
    const search = window.location.search.replace('?', '');
    const searchParamObj = new URLSearchParams(search);
    const paramsObject = Object.fromEntries(searchParamObj.entries());
    // Strip PHP/array-style bracket notation (e.g. `utm_source[]=google`) before
    // mapping web_* -> utm_* and serializing into spid_trial_src_ref__c. Without
    // this the bracketed key never matches `utm_source`, leaving latest_utm_*
    // empty and emitting a malformed `utm_source[]=...` that Salesforce cannot
    // parse for the lead source. Runs before updateParams so any `web_*[]`
    // variants still fall back through the web_* -> utm_* mapping.
    const normalizedParams = normalizeArrayParams(paramsObject);
    const updatedParams = updateParams(normalizedParams);
    return updatedParams;
  }
  return null;
};

export {
  searchHasNovalidate,
  latchTrialNovalidateFromWindow,
  promoteTrialNovalidateFromSearch,
  pageUrlHasNovalidate as isNovalidateQuery
} from './novalidateQuery';

/** Hide leftover Yup helper text so a late Formik flush cannot flash 499xx on novalidate. */
export const hideFrontendValidationHints = () => {
  document.querySelectorAll(
    '[id*="helper-text"], .MuiFormHelperText-root.Mui-error'
  ).forEach((el) => {
    el.classList.add('hidden');
  });
};

/** Path segment for the standalone AEM marketing trial page (not `/trial/aem-remote-support`). */
export const TRIAL_AEM_PAGE_PATH = '/trial/aem';

/**
 * True when the pathname is `/trial/aem` or a locale-prefixed equivalent, e.g. `/zh-Hans/trial/aem`.
 * Trailing slashes are ignored. Does not match `/trial/aem-remote-support` (does not end with this segment).
 */
export const isTrialAemPage = () => {
  if (!isBrowser || typeof window === 'undefined') {
    return false;
  }
  const path = window.location.pathname.replace(/\/+$/, '') || '/';
  return path.endsWith(TRIAL_AEM_PAGE_PATH);
};

/** Path segment for the Remote Access trial page. */
export const TRIAL_REMOTE_ACCESS_PAGE_PATH = '/trial/remote-access';

/**
 * True when the pathname is `/trial/remote-access` or a locale-prefixed equivalent,
 * e.g. `/zh-Hans/trial/remote-access`. Trailing slashes are ignored.
 */
export const isRemoteAccessTrialPage = () => {
  if (!isBrowser || typeof window === 'undefined') {
    return false;
  }
  const path = window.location.pathname.replace(/\/+$/, '') || '/';
  return path.endsWith(TRIAL_REMOTE_ACCESS_PAGE_PATH);
};

const emailMatchesRegex = (email) => {
  const match = email?.match(/^[A-Z0-9_\.%\+\-\']+@(?:[A-Z0-9\-]+\.)+(?:[A-Z]{2,25})$/i);
  return match;
};

// Reset reCAPTCHA
export const resetReCAPTCHA = () => {
  // Clear the token override
  if (typeof window !== 'undefined') {
    window.reCAPTCHAResponseOverride = '';
  }
  // If reCAPTCHA is loaded
  if ('grecaptcha' in window && 'reset' in window.grecaptcha) {
    // Reset the reCAPTCHA widget
    window.grecaptcha.reset();
  }
};

const MARKETING_WARNING_ALIASES = {
  40402: '41405',
  // Older submit.php sent 49925 for empty company; current API sends 49932.
  49925: '49932'
};

/**
 * Copy for a marketing-API / trial-API result code. Prefers Contentful
 * `messages.warning[code]`, then a known alias (e.g. 40402 → 41405,
 * legacy 49925 → 49932), then `response.messages[0]`, then `warning.default`.
 */
export const resolveMarketingApiWarning = (response, messages = {}) => {
  const warning = messages.warning || {};
  const code = String(response?.result ?? '');
  const alias = MARKETING_WARNING_ALIASES[code];
  const fromContent = warning[code] || (alias ? warning[alias] : undefined);
  if (typeof fromContent === 'string' && fromContent.length > 0) {
    return fromContent;
  }
  const apiMessages = response?.messages;
  if (Array.isArray(apiMessages)) {
    const first = apiMessages.find((msg) => typeof msg === 'string' && msg.trim());
    if (first) {
      return first;
    }
  }
  return warning.default;
};

export const apiErrorDisplay = (message, content) => {
  let warningMessage = message;
  if (typeof message === 'undefined') warningMessage = content.messages.warning.default;
  if (typeof message === 'string') {
    warningMessage = warningMessage.replace(/[{{{}}}}}}]/g, '');
  }
  const warning = document?.querySelector('.alert-warning');
  if (warning) {
    const classList = warning.classList;
    if ((typeof warningMessage === 'string') && ([...classList].includes('hidden'))) {
      warning.classList.remove('hidden');
      warning.querySelector('.alert-warning_content').innerHTML = warningMessage;
    } else if (message?.target?.tagName === 'BUTTON') {
      warning.classList.add('hidden');
    }
  }
};

/**
 * Drop Yup errors for fields that are not shown (unrendered, or whose
 * `.form-group` has class `hidden`). Pure: returns a new map; does not mutate
 * Formik state — callers should `formik.setErrors` the result.
 *
 * Never drops `phone`. The intl-tel-input often lacks a stable `name` until
 * `fieldName` is set, and treating a missing node as "hidden" let the
 * enterprise form POST with an empty required phone while the error text still
 * rendered from React state.
 *
 * @param {object} [errors={}] Formik/Yup error map
 * @returns {object}
 */
export const removeHiddenFieldErrors = (errors = {}) => {
  if (!errors || typeof errors !== 'object') {
    return {};
  }
  const next = { ...errors };
  Object.keys(next).forEach((field) => {
    if (field === '2fa_code' || field === 'phone') {
      return;
    }
    let errorEl = null;
    try {
      errorEl = document.querySelector(`[name="${field}"]`);
    } catch {
      return;
    }
    const parentFormEl = errorEl?.closest('.form-group');
    const isHiddenGroup = !!(parentFormEl && parentFormEl.classList.contains('hidden'));
    const isMissingFromDom = !errorEl;
    if (isHiddenGroup || isMissingFromDom) {
      delete next[field];
    }
  });
  return next;
};

/** Mark every Formik value as touched without re-validating (submit-time error display). */
export const touchAllFormikFields = (formik) => {
  const touched = Object.keys(formik.values || {}).reduce((acc, key) => {
    acc[key] = true;
    return acc;
  }, {});
  formik.setTouched(touched, false);
};

const objectToUrlParams = (obj) => {
  const params = new URLSearchParams();
  Object.keys(obj).forEach((key) => {
    if (Object.hasOwn(obj, key)) {
      params.append(key, obj[key]);
    }
  });
  return params.toString();
};

/**
 * Plain `/trial/aem` (and similar): no combined/radio product picker — AEM is implicit.
 * `I_want_to_use_Splashtop_for` should submit as `aem`, not a Contentful combined-product key from cookies.
 */
export const isImplicitStandaloneAemTrial = (v) => (
  typeof v === 'string'
  && !isRetrialVariation(v)
  && getBaseVariation(v) === 'aem'
  && !isAemRemoteSupportDualProductVariation(v)
  && !isCombinedStyleTrialProductPicker(v)
  && v.indexOf('radio') === -1
);

/** Use endpoints quantity field + howManyEndpoints UI (vs technicians) */
export const useEndpointsSupportQuantityField = (thisVariation, iWantToUseValue, inputs) => {
  const autonomous = inputs && getAutonomousEndpointProductValue(inputs);
  // Two-radio AEM / Remote Support page: endpoints only when Autonomous product is selected
  if (isAemRemoteSupportDualProductVariation(thisVariation)) {
    return !!autonomous && isAutonomousProductSelection(iWantToUseValue, autonomous);
  }
  // Combined / new-site-radio: endpoints only when the third product (autonomous) is selected — not Remote Support
  if (isCombinedStyleTrialProductPicker(thisVariation)) {
    return !!autonomous && isAutonomousProductSelection(iWantToUseValue, autonomous);
  }
  // Standalone AEM forms (no combined product picker): schema uses endpoints only
  if (usesStandaloneAemTrialFields(thisVariation)) {
    return true;
  }
  return false;
};

/** Third product radio: radio combined surfaces when Contentful defines a third product key */
export const showCombinedAutonomousEndpointRadio = (variation, inputs) => (
  variation.indexOf('radio') > -1
  && isCombinedStyleTrialProductPicker(variation)
  && !!getAutonomousEndpointProductValue(inputs)
);

// this gets the values from the cookie on page load
// and sets the product info for dropdown / radio combination fields
// and set label classes to shrink above field if populated
export const setProductInfo = (formik, newValues, dropdownRefsObj, thisVariation = '', inputs = null) => {
  setTimeout(() => {
    if (document.readyState === 'complete') {
      const useFor = newValues.I_want_to_use_Splashtop_for;
      if (inputs) {
        formik.setFieldValue('lead_source', resolveLeadSource(thisVariation, useFor, inputs));
      }
      const supportFieldKey = useEndpointsSupportQuantityField(thisVariation, useFor, inputs)
        ? 'how_many_endpoints_need_to_manage'
        : 'how_many_technicians_provide_support';
      const supportRestored = newValues[supportFieldKey] ?? newValues.how_many_technicians_provide_support;
      const dropDownsValues = {
        I_want_to_use_Splashtop_for: newValues['I_want_to_use_Splashtop_for'],
        How_many_users_need_remote_access: newValues['How_many_users_need_remote_access'],
        [supportFieldKey]: supportRestored,
        which_best_describes_you: newValues['which_best_describes_you'],
        which_best_describes_your_role: newValues['which_best_describes_your_role']
      };
      const usingSplashtopFor = dropdownRefsObj.usingSplashtopRef.current;
      const howManyUsersSelect = dropdownRefsObj.howManyUsersRef.current;
      const howManyTechniciansSelect = dropdownRefsObj.howManyTechsRef.current;
      const whichBestDescribesYouSelect = dropdownRefsObj.whichBestDescribesYouRef.current;
      const whatIsYourRoleSelect = dropdownRefsObj.whatIsYourRoleRef.current;
      Object.keys(dropDownsValues).forEach((key) => {
        // sets dropdown label in UI
        if (!!usingSplashtopFor
          && key === 'I_want_to_use_Splashtop_for'
          && dropDownsValues[key] !== key) {
          const selectValueLabel = usingSplashtopFor.parentElement.querySelector('label');
          if (dropDownsValues[key] && dropDownsValues[key].length > 0) {
            selectValueLabel.classList.add('has-value');
            formik.setFieldValue('I_want_to_use_Splashtop_for', dropDownsValues[key]);
          } else {
            selectValueLabel.classList.remove('has-value');
            formik.setFieldValue('I_want_to_use_Splashtop_for', '');
          }
        }
        // separated these and reset formik.touched for the field if no value
        if (!!howManyUsersSelect
          && key === 'How_many_users_need_remote_access'
          && dropDownsValues[key] !== key) {
          const selectValueLabel = howManyUsersSelect?.parentElement.querySelector('label');
          if (typeof dropDownsValues[key] !== 'undefined' && dropDownsValues[key].length > 0) {
            selectValueLabel?.classList.add('has-value');
            formik.setFieldValue('How_many_users_need_remote_access', dropDownsValues[key]);
          } else {
            selectValueLabel.classList.remove('has-value');
            formik.setFieldValue('How_many_users_need_remote_access', '');
          }
        } else {
          formik.setFieldTouched('How_many_users_need_remote_access', false);
        }

        if (!!howManyTechniciansSelect
          && key === supportFieldKey
          && dropDownsValues[key] !== key) {
          const selectValueLabel = howManyTechniciansSelect?.parentElement.querySelector('label');
          if (typeof dropDownsValues[key] !== 'undefined' && dropDownsValues[key].length > 0) {
            selectValueLabel?.classList.add('has-value');
            formik.setFieldValue(supportFieldKey, dropDownsValues[key]);
          } else {
            selectValueLabel.classList.remove('has-value');
            formik.setFieldValue(supportFieldKey, '');
          }
        } else {
          formik.setFieldTouched(supportFieldKey, false);
        }

        if (!!whichBestDescribesYouSelect
          && key === 'which_best_describes_you'
          && dropDownsValues[key] !== key) {
          const selectValueLabel = whichBestDescribesYouSelect?.parentElement.querySelector('label');
          if (typeof dropDownsValues[key] !== 'undefined' && dropDownsValues[key].length > 0) {
            selectValueLabel?.classList.add('has-value');
            formik.setFieldValue('which_best_describes_you', dropDownsValues[key]);
          } else {
            selectValueLabel.classList.remove('has-value');
            formik.setFieldValue('which_best_describes_you', '');
          }
        } else {
          formik.setFieldTouched('which_best_describes_you', false);
        }

        if (!!whatIsYourRoleSelect
          && key === 'which_best_describes_your_role'
          && dropDownsValues[key] !== key) {
          const selectValueLabel = whatIsYourRoleSelect?.parentElement.querySelector('label');
          if (typeof dropDownsValues[key] !== 'undefined' && dropDownsValues[key].length > 0) {
            selectValueLabel?.classList.add('has-value');
            formik.setFieldValue('which_best_describes_your_role', dropDownsValues[key]);
          } else {
            selectValueLabel.classList.remove('has-value');
            formik.setFieldValue('which_best_describes_your_role', '');
          }
        } else {
          formik.setFieldTouched('which_best_describes_your_role', false);
        }
      });
    }
  }, 500);
};

// Update forgot password link
export const updateForgotPasswordLink = (email, forgotPassworkLink) => {
  // Start with basic forgot password link
  setTimeout(() => {
    if (isBrowser) {
      // Add email if it's valid
      let linkVal = forgotPassworkLink?.href;
      linkVal = `${linkVal}?src=web`;
      if (((typeof email) === 'string')
        && emailMatchesRegex(email)
        && emailMatchesRegex(email)[0] === email) {
        // Add base 64 encoded email
        linkVal = `${linkVal}&email=${btoa(email)}`;
      }

      // Add current path
      let path = window.location.pathname;
      const params = getTrialParams();

      if (params) {
        path = `${path}?${objectToUrlParams(params)}`;
      }
      if (path.length) {
        // Add URL encoded path
        linkVal = `${linkVal}&path=${encodeURIComponent(path)}`;
      }

      // Set the link
      forgotPassworkLink.setAttribute('href', linkVal);
    }
  }, 500);
};

// Get the Hubspot identifier from the hutk cookie
export const getHubspotIdentifier = () => {
  // Get the hutk cookie value
  console.log('inside getHubspot function before browser check:::');
  if (isBrowser) {
    const hutk = Cookies.get('hubspotutk');
    console.log('inside getHubspotIdentifier:::', hutk);
    if ((typeof hutk === 'string' && hutk.length > 0)) return hutk;
  }
  return '';
};

// Check the URL to see if the params contain a string (ex: ?testdata=1)
const urlParamsContain = (input) => isBrowser && window.location.search.includes(input);
// env=live&utm_source=direct&utm_medium=direct&web_source=wrong&web_medium=wrong&platform=my"

// Get override from URL
export const getOverride = (name, allowlist) => {
  // If the URL parameters contain the override
  if (isBrowser && urlParamsContain(`${name}=`)) {
    const params = getQueryStrings();
    if (name in params) {
      const value = params[name];
      // If there is an allowlist
      if (Array.isArray(allowlist) && allowlist.length) {
        // If the value is in allowlist
        if (allowlist.indexOf(value) > -1) {
          return value;
        }
        return undefined;
      }
      return value;
    }
  }
  return undefined;
};

// Get the environment (live or dev)
export const getEnvironment = () => {
  // Use the override if it exists
  const envOverride = getOverride('env', ['live', 'dev', 'local']);
  if (envOverride) return envOverride;

  // If using a localhost domain name
  if (isBrowser && window.location.host.includes('localhost')) {
    return 'local';
  }

  // If not using a production domain name (including.com or .cn)
  if (isBrowser && !(window.location.host.includes('www.splashtop.c'))) {
    return 'dev';
  }
  return 'live';
};

export const newSpidAndAbTestParam = (spid, abTests) => {
  const spidAbTestsReturn = {
    spid: '',
    abTests: ''
  };

  if (isBrowser) { // spid, abTests
    const paramsObject = Object.fromEntries(spid.entries());
    const abtestInParams = Object.keys(paramsObject).includes('abtest');
    let paramABTestValue = paramsObject.abtest;
    if (!!paramABTestValue && paramABTestValue.includes('%2C')) {
      paramABTestValue = paramABTestValue.split('%2C').join(',');
    }

    const abTestsArray = Array.isArray(abTests) ? abTests : [];
    const abtestParamToAdd = mergeDedupeSortAbTests(abTestsArray, paramABTestValue, abtestInParams);

    if (abtestParamToAdd.length > 0) {
      paramsObject.abtest = abtestParamToAdd;
      let newSpidVal = new URLSearchParams(paramsObject).toString();
      newSpidVal = newSpidVal.replaceAll('%2C', ',');
      spidAbTestsReturn.spid = newSpidVal;
      spidAbTestsReturn.abTests = abtestParamToAdd;
    }
  }
  return spidAbTestsReturn;
};

// Always read from window so every caller and every module copy shares the same Set (no cache).
const WINDOW_SENT_KEYS_KEY = '__flexibleFormSentEventKeys';

function getSentEventKeys() {
  if (!isBrowser || typeof window === 'undefined') return new Set();
  if (!window[WINDOW_SENT_KEYS_KEY]) window[WINDOW_SENT_KEYS_KEY] = new Set();
  return window[WINDOW_SENT_KEYS_KEY];
}

// Normalize payload: second arg can be { abTests, region, variation } or legacy variation string
const normalizePayload = (payload) => (
  typeof payload === 'string' && payload.length > 0 ? { variation: payload } : payload
);

/** Display name for the AEM / autonomous-endpoint product in the products-chosen success value. */
export const AEM_PRODUCT_DISPLAY_NAME = 'AEM standalone';

/**
 * Maps a Formik product value to its analytics display name:
 * Remote Access → "Remote Access", Remote Support → "Remote Support",
 * `aem` / Contentful autonomous-endpoint key → "AEM standalone".
 */
const getProductDisplayName = (productValue, inputs) => {
  if (!productValue || typeof productValue !== 'string') return '';
  if (productValue === CHECKBOX_PRODUCT_REMOTE_ACCESS) return CHECKBOX_PRODUCT_REMOTE_ACCESS;
  if (productValue === CHECKBOX_PRODUCT_REMOTE_SUPPORT) return CHECKBOX_PRODUCT_REMOTE_SUPPORT;
  const autonomousKey = inputs ? getAutonomousEndpointProductValue(inputs) : null;
  if (isAutonomousProductSelection(productValue, autonomousKey)) return AEM_PRODUCT_DISPLAY_NAME;
  return productValue;
};

/**
 * Product(s) chosen value sent with the `success` analytics event (Form_Selected_Products).
 * - Checkbox multi-select picker (`/trial/choose?abTest=checkboxSelection`): comma-separated
 *   display names of every selected product.
 * - Single-select picker (combined dropdown / radio-combined / aem-remote-support): the one
 *   chosen product.
 * - Single-product trials with no picker: derived from the variation — `sos` → "Remote Support",
 *   `sba` → "Remote Access", standalone `aem` → "AEM standalone".
 * Product display names: "Remote Access", "Remote Support", "AEM standalone".
 */
export const getProductsChosenLabel = (variation, formValues = {}, inputs = null) => {
  if (typeof variation !== 'string' || variation.length === 0) return '';

  if (isCheckboxCombinedVariation(variation)) {
    return filterValidSelectedProductValues(formValues.selected_products)
      .map((product) => getProductDisplayName(product, inputs))
      .filter(Boolean)
      .join(',');
  }

  if (isSingleSelectProductPickerVariation(variation)) {
    return getProductDisplayName(formValues.I_want_to_use_Splashtop_for, inputs);
  }

  if (isSosVariation(variation)) return CHECKBOX_PRODUCT_REMOTE_SUPPORT;
  if (isSbaVariation(variation)) return CHECKBOX_PRODUCT_REMOTE_ACCESS;
  if (usesStandaloneAemTrialFields(variation) || isAemVariation(variation)) {
    return AEM_PRODUCT_DISPLAY_NAME;
  }

  return getProductDisplayName(formValues.I_want_to_use_Splashtop_for, inputs);
};

/**
 * Capture window-size and submit-button position at a given moment (typically
 * "form loaded"). GA4 only records screen size, not window size, so sending
 * `window.innerHeight` through GTM lets analytics see the real browser
 * viewport. The `submitVisibleOnLoad` flag records whether the trial-form
 * submit button was fully on screen at load without the user scrolling, which
 * is how form-conversion correlates with viewport in reality.
 *
 * Returns null in SSR / when `window` is missing. When the submit button is
 * not yet in the DOM, viewport fields are populated but submit-button fields
 * are left null so downstream serialization can skip them cleanly.
 */
export const measureFormViewportMetrics = () => {
  if (!isBrowser || typeof window === 'undefined' || typeof document === 'undefined') {
    return null;
  }
  const windowInnerHeight = typeof window.innerHeight === 'number' ? window.innerHeight : null;
  const windowInnerWidth = typeof window.innerWidth === 'number' ? window.innerWidth : null;
  // The trial form has id="ftf" and the submit button lives inside
  // SubmitButton's `.buttonContainer` as `button[type="submit"][name="submit"]`.
  // Scoping to the trial form avoids matching unrelated submit buttons
  // elsewhere on the page (e.g. search).
  let submitVisibleOnLoad = null;
  let submitButtonTopOnLoad = null;
  const submitButton = document.querySelector(
    '#ftf .buttonContainer button[type="submit"]'
  ) || document.querySelector('.flexible-form .buttonContainer button[type="submit"]');
  if (submitButton && typeof submitButton.getBoundingClientRect === 'function') {
    const rect = submitButton.getBoundingClientRect();
    if (rect && typeof rect.top === 'number' && typeof rect.bottom === 'number') {
      submitButtonTopOnLoad = Math.round(rect.top);
      if (typeof windowInnerHeight === 'number' && rect.height > 0) {
        submitVisibleOnLoad = rect.top >= 0 && rect.bottom <= windowInnerHeight;
      }
    }
  }
  return {
    windowInnerHeight,
    windowInnerWidth,
    submitVisibleOnLoad,
    submitButtonTopOnLoad
  };
};

// Send Google Analytics form event. payload: { abTests, region, variation } or legacy variation string.
// options.skipDedupe: if true, allow repeated sends (e.g. validation errors on each submit).
// options.formType: 'signup' | 'signin' — when present and event is success (empty), label becomes "success triggered (empty) using signup" or "using signin".
// options.productsChosen: product(s) chosen string attached to the success send as Form_Selected_Products.
// options.viewportMetrics: { windowInnerHeight, windowInnerWidth, submitVisibleOnLoad, submitButtonTopOnLoad }
//   captured via measureFormViewportMetrics(). Threaded through to the Flexible_Form rudder payload
//   as Form_Window_Inner_Height / Form_Window_Inner_Width / Form_Submit_Visible_On_Load /
//   Form_Submit_Button_Top_On_Load. Typically attached to the "form loaded" send.
export const sendGAFormEvent = (response, payload, options = {}) => {
  const normalized = normalizePayload(payload);
  const variation = normalized?.variation;
  if (!isBrowser || !variation || (typeof variation === 'string' && variation.length === 0)) {
    return;
  }
  let eventKey;
  if (typeof response === 'string') {
    eventKey = response;
  } else {
    let result = response?.result;
    /* eslint-disable-next-line eqeqeq */
    if (result == '20200') result = 'success';
    const allMessages = (response?.messages) ? response?.messages.join(', ') : 'empty';
    const baseLabel = `${result} triggered (${allMessages})`;
    const formTypeSuffix = (result === 'success' && allMessages === 'empty' && (options.formType === 'signup' || options.formType === 'signin'))
      ? ` using ${options.formType}`
      : '';
    eventKey = baseLabel + formTypeSuffix;
  }
  const sentEventKeys = getSentEventKeys();
  if (!options.skipDedupe) {
    if (sentEventKeys.has(eventKey)) return;
    sentEventKeys.add(eventKey);
  }

  const gaFormPathName = `new-${variation}`;
  const extraProps = {
    abTests: normalized.abTests,
    region: normalized.region,
    variation: normalized.variation
  };
  const vm = options.viewportMetrics;
  if (vm && typeof vm === 'object') {
    if (typeof vm.windowInnerHeight === 'number') extraProps.windowInnerHeight = vm.windowInnerHeight;
    if (typeof vm.windowInnerWidth === 'number') extraProps.windowInnerWidth = vm.windowInnerWidth;
    if (typeof vm.submitVisibleOnLoad === 'boolean') extraProps.submitVisibleOnLoad = vm.submitVisibleOnLoad;
    if (typeof vm.submitButtonTopOnLoad === 'number') extraProps.submitButtonTopOnLoad = vm.submitButtonTopOnLoad;
  }
  if (typeof response === 'string') {
    sendFlexibleFormEventImmediate(gaFormPathName, response, extraProps);
    return;
  }

  // Override result sent in the event without changing response
  let result = response?.result;
  /* eslint-disable-next-line eqeqeq */
  if (result == '20200') {
    result = 'success';
  }

  // Attach the product(s) chosen to the success send only (e.g. "Remote Access, Remote Support").
  if (result === 'success' && typeof options.productsChosen === 'string' && options.productsChosen.length > 0) {
    extraProps.selectedProducts = options.productsChosen;
  }

  const allMessages = (response?.messages) ? response?.messages.join(', ') : 'empty';
  const label = eventKey;
  // Same args as former sendToGtm('Flexible Form', gaFormPathName, label, extraProps): helper
  // already prefixes category 'Flexible Form' — do not pass it here (would shift Form_Variation / Form_Event).
  sendFlexibleFormEventImmediate(gaFormPathName, label, extraProps);
};

const frontEndErrors = {
  'I_want_to_use_Splashtop_for': '49981-I_want_to_use_Splashtop_for',
  'How_many_users_need_remote_access': '49981-How_many_users_need_remote_access',
  'how_many_technicians_provide_support': '49981-how_many_technicians_provide_support',
  'how_many_endpoints_need_to_manage': '49981-how_many_endpoints_need_to_manage',
  'which_best_describes_you': '49981-which_best_describes_you',
  'which_best_describes_your_role': '49981-which_best_describes_your_role',
  'email': '49930-Please enter a valid email.',
  'password': '49936-Please enter your password.',
  'first_name': '49921-Please enter a valid first name.',
  'last_name': '49922-Please enter a valid last name.',
  'terms-agreement': '49940-Please agree to Splashtop Terms of Service and Privacy Policy.',
  'phone': '49923-Please enter your phone number.',
  '2fa_code': '41405-The verification code is incorrect or expired.',
  'reCAPTCHA': '49980-Please complete the reCAPTCHA challenge.'
};

const frontEndErrorsArrayAccess = [
  'email',
  'password',
  'first_name',
  'last_name',
  'How_many_users_need_remote_access',
  'terms-agreement',
  'phone',
  '2fa_code',
  'reCAPTCHA'
];

const frontEndErrorsArraySupport = [
  'email',
  'password',
  'first_name',
  'last_name',
  'how_many_technicians_provide_support',
  'how_many_endpoints_need_to_manage',
  'terms-agreement',
  'phone',
  '2fa_code',
  'reCAPTCHA'
];

const frontEndErrorsArrayChoose = ['I_want_to_use_Splashtop_for',
  'How_many_users_need_remote_access',
  'how_many_technicians_provide_support',
  'how_many_endpoints_need_to_manage',
  'which_best_describes_you',
  'which_best_describes_your_role',
  'email',
  'password',
  'first_name',
  'last_name',
  'terms-agreement',
  'phone',
  '2fa_code',
  'reCAPTCHA'
];

export const gaSendTopError = (errorKeys, payload) => {
  /* eslint-disable arrow-body-style, no-nested-ternary */
  const variation = payload?.variation;
  if (!variation) return;
  const baseVariation = getBaseVariation(variation);
  const frontEndErrorsArray = baseVariation.includes('sba')
    ? frontEndErrorsArrayAccess
    : (baseVariation.includes('sos') || usesStandaloneAemTrialFields(variation))
      ? frontEndErrorsArraySupport
      : frontEndErrorsArrayChoose;
  const lowestIndex = errorKeys.reduce((lowest, errorKey) => {
    return frontEndErrorsArray.indexOf(errorKey) < lowest
      ? frontEndErrorsArray.indexOf(errorKey)
      : lowest;
  }, frontEndErrorsArray.length);
  const errorMessage = frontEndErrors[frontEndErrorsArray[lowestIndex]];
  sendGAFormEvent(errorMessage, payload, { skipDedupe: true });
};

export const handleFormInteracted = (payload, name) => {
  if (name === undefined || !payload?.variation) return;
  // "first interaction" is sent once from the form-loaded effect in TrialForm/TrialFormSteps; skip here
  if (name === 'first interaction') return;
  console.log('trialFormUtils handleFormInteracted:::', payload.variation, name);
  sendGAFormEvent(`form interacted ${name}`, payload);
};

// AEM `how_many_endpoints_need_to_manage` dropdown: the Contentful option set
// splits the small-fleet bucket into two display-only options ("0-24" and
// "25-100") so very-small MSPs can distinguish themselves from small ones in
// marketing analytics, while the downstream marketing/trial API still expects
// the single collapsed bucket value "0-100". The <select> option values and
// Formik state intentionally keep the two display keys distinct — browsers
// cannot visually differentiate two <option> elements that share a `value`,
// and Formik needs unique values to track and restore the chosen row — so the
// collapse happens at POST time only, via `toEndpointsApiValue` in
// `createFormData`. Any key not listed here passes through unchanged.
export const AEM_ENDPOINTS_DISPLAY_TO_API_VALUE = {
  '0-24': '0-100',
  '25-100': '0-100',
};

export const toEndpointsApiValue = (displayValue) => (
  Object.prototype.hasOwnProperty.call(AEM_ENDPOINTS_DISPLAY_TO_API_VALUE, displayValue)
    ? AEM_ENDPOINTS_DISPLAY_TO_API_VALUE[displayValue]
    : displayValue
);

// The two AEM endpoints display buckets that COLLAPSE into `0-100` at POST
// time (see `AEM_ENDPOINTS_DISPLAY_TO_API_VALUE`) submit as the same opaque
// value, so the backend lead data cannot tell which of the two the user
// actually clicked. Marketing wants that signal preserved for attribution,
// so we mirror the raw dropdown pick onto the `spid_trial_src_ref__c`
// query string as an `endpointselect=<displayBucket>` key/value — a
// parameter name that stays out of the way of utm_*, latest_utm_*,
// platform, abtest, etc. Only the two collapsed buckets contribute a
// value here; `101-250` and up pass through unchanged as
// `how_many_endpoints_need_to_manage` already and need no shadow signal.
// The signal lives on Formik state (not just the eventual POST) so it
// reflects the current selection even for a visitor whose submission is
// blocked by the below-minimum guard or who abandons before clicking
// submit — downstream telemetry (persisted form state, analytics beacons)
// still sees which bucket they chose.
export const AEM_ENDPOINTS_DISPLAY_TO_TRACKING_VALUE = {
  '0-24': '0to24',
  '25-100': '25-100',
};

export const toEndpointsTrackingValue = (displayValue) => (
  Object.prototype.hasOwnProperty.call(AEM_ENDPOINTS_DISPLAY_TO_TRACKING_VALUE, displayValue)
    ? AEM_ENDPOINTS_DISPLAY_TO_TRACKING_VALUE[displayValue]
    : null
);

/**
 * Return a new `spid_trial_src_ref__c` query-string value that reflects
 * the current endpoints dropdown pick. Any pre-existing `endpointselect=…`
 * fragment is stripped first (so the function is idempotent and safe to
 * call every time the dropdown changes — picking `0-24` then `25-100`
 * yields `endpointselect=25-100`, not both). Buckets outside the collapse
 * map (`101-250` and up) and an empty selection emit no `endpointselect=`
 * at all, so non-AEM / unambiguous submissions carry no tracking noise.
 * Tolerates an initially-empty base so the resulting fragment never
 * starts with `&`.
 */
export const appendEndpointSelectToSpidSrcRef = (spidSrcRef, displayValue) => {
  const base = typeof spidSrcRef === 'string' ? spidSrcRef : '';
  const strippedBase = base.length > 0
    ? base.split('&').filter((pair) => !pair.startsWith('endpointselect=')).join('&')
    : '';
  const trackingValue = toEndpointsTrackingValue(displayValue);
  if (!trackingValue) return strippedBase;
  const separator = strippedBase.length > 0 ? '&' : '';
  return `${strippedBase}${separator}endpointselect=${trackingValue}`;
};
